import { withRetry } from '@/utils/withRetry';
import { graphError } from './graph-request-error';
import { GRAPH_RETRY_OPTIONS } from './graph-retry';

export interface UploadInChunksOptions {
  /** Must be a multiple of 320 KiB — Graph rejects anything else for a non-final chunk. */
  chunkSize: number;
  signal?: AbortSignal;
  /**
   * Wording for a failed chunk PUT. Defaults to the drive upload's original message, so lifting this
   * function out of sharepoint-drive-service.ts left every existing assertion on that text intact.
   */
  errorMessage?: string;
}

/**
 * PUTs a Blob to a Graph upload session as sequential ranged chunks, and resolves with the FINAL
 * response — unread.
 *
 * Parsing nothing is the whole point of the return type, and it is why this exists as a shared
 * function rather than two near-copies. A *drive* upload's last PUT returns a DriveItem body; an
 * Outlook *attachment* upload session's last PUT returns 201 with an EMPTY body and a `Location`
 * header. The original in-service loop parsed `.json()` on every chunk, which would throw a
 * SyntaxError on that empty body — neither a GraphRequestError nor a TypeError, therefore not
 * retryable, and reported to the user as a failed attachment that had in fact succeeded. Each caller
 * now reads the final response the way its own resource answers.
 *
 * Shared by both because the protocol is identical: re-PUTting the same Content-Range byte range
 * against the same uploadUrl is the documented Graph recovery path for this resumable,
 * range-addressed format — so each chunk is safely retryable on its own, independently of the others.
 */
export async function uploadInChunks(
  uploadUrl: string,
  blob: Blob,
  { chunkSize, signal, errorMessage }: UploadInChunksOptions,
): Promise<Response> {
  const message = errorMessage ?? 'Failed to upload SharePoint file chunk';

  const putChunk = (contentRange: string, chunk: Blob): Promise<Response> =>
    withRetry(
      async () => {
        // The upload-session URL is itself pre-authenticated by Graph — no bearer token needed.
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Range': contentRange },
          body: chunk,
          // Conditional so a caller with no signal sends a byte-identical request object to what the
          // drive upload sent before this function existed.
          ...(signal ? { signal } : {}),
        });

        if (!response.ok) {
          throw graphError(message, response);
        }

        return response;
      },
      { ...GRAPH_RETRY_OPTIONS, signal },
    );

  const total = blob.size;

  // A zero-byte file can't be expressed as a byte-range chunk (bytes 0--1/0 is malformed). Graph
  // finalizes an empty file with a single PUT: an empty body and Content-Range "bytes */0".
  //
  // OneDrive/SharePoint only. Outlook rejects an AttachmentItem declaring size 0 outright, so the mail
  // path never reaches this branch — it is kept because the drive path genuinely needs it, not because
  // it is shared.
  if (total === 0) {
    return putChunk('bytes */0', blob.slice(0, 0));
  }

  let start = 0;
  let lastResponse: Response;
  do {
    const end = Math.min(start + chunkSize, total);
    lastResponse = await putChunk(`bytes ${start}-${end - 1}/${total}`, blob.slice(start, end));
    start = end;
  } while (start < total);

  return lastResponse;
}
